import { useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/components/ui/cn';
import { IconLogout, IconChevron, IconSidebar } from '@/components/ui/icons';
import { accessibleRoutes, resolveNavGroups } from './adminNav';
import { adminLogout, getAdminProfile, getAdminPermissions } from '@/lib/adminAuth';

/** Sidebar collapse is a per-browser UI preference, persisted like the auth
 *  cache (localStorage, best-effort — private mode just falls back to default).
 *  Collapsed = an icon-only rail; expanded = the full grouped nav. */
const SIDEBAR_KEY = 'telride.admin.sidebar.collapsed';

function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSidebarCollapsed(v: boolean) {
  try {
    localStorage.setItem(SIDEBAR_KEY, v ? '1' : '0');
  } catch {
    /* ignore — non-persistent is fine */
  }
}

/** The authenticated Admin frame: a LIGHT sidebar (white, hairline right border,
 *  Laravel-style GROUPED nav — small uppercase muted category headers over their
 *  items, with collapsible groups and an indigo-tinted active item) + a clean
 *  white topbar (page title on the left, admin identity + logout on the right),
 *  and the routed Admin content. The nav is gated by the staffer's permissions
 *  (an item only shows if the role can read that resource). */
export function AdminShell({ children }: { children: ReactNode }) {
  const profile = getAdminProfile();
  const perms = getAdminPermissions();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);

  const toggleSidebar = () =>
    setCollapsed((v) => {
      const next = !v;
      writeSidebarCollapsed(next);
      return next;
    });

  // Strictly what this role can open. Dashboard used to be force-shown here,
  // which handed roles without dashboard.read a link straight into a 403.
  const routes = accessibleRoutes(perms);
  const groups = resolveNavGroups(routes);

  // Page title for the topbar — the label of the route whose path the URL is on.
  // Treat '/' as Dashboard (the index route renders it).
  const active = routes.find(
    (r) =>
      location.pathname === r.to ||
      location.pathname.startsWith(r.to + '/') ||
      (location.pathname === '/' && r.to === '/dashboard'),
  );
  const pageTitle = active?.label ?? 'Admin';

  const initials = (profile?.name || profile?.email || 'T')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');

  return (
    <div className="flex h-screen overflow-hidden bg-app">
      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <aside
        className={cn(
          'flex h-full shrink-0 flex-col border-r border-hairline bg-surface transition-[width] duration-200 ease-in-out',
          collapsed ? 'w-16' : 'w-64',
        )}
      >
        {/* Brand — text hides when collapsed, leaving the centred "T" mark. */}
        <div
          className={cn(
            'flex h-16 items-center gap-2.5',
            collapsed ? 'justify-center px-0' : 'px-5',
          )}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-ink0 text-sm font-bold text-white">
            T
          </div>
          {!collapsed ? (
            <div className="leading-tight">
              <div className="text-[15px] font-semibold text-text-primary">Telride</div>
              <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-text-muted">
                Admin
              </div>
            </div>
          ) : null}
        </div>

        {/* Grouped nav — collapsible categories, gated to readable resources.
         *  When the rail is collapsed, group headers drop away and items render
         *  as a centred icon stack with the label exposed via a native tooltip. */}
        <nav
          className={cn(
            'flex-1 overflow-y-auto overflow-x-hidden pb-6 pt-1',
            collapsed ? 'px-2' : 'px-3',
          )}
        >
          {groups.map((group) => (
            <NavGroup key={group.label} label={group.label} collapsed={collapsed}>
              {group.items.map((r) => {
                const Icon = r.icon;
                return (
                  <NavLink
                    key={r.to}
                    to={r.to}
                    title={collapsed ? r.label : undefined}
                    className={({ isActive }) =>
                      cn(
                        'nav-item',
                        isActive && 'nav-item-active',
                        collapsed && 'justify-center px-0',
                      )
                    }
                  >
                    <Icon className="h-[18px] w-[18px] shrink-0" />
                    {!collapsed ? <span className="flex-1 truncate">{r.label}</span> : null}
                  </NavLink>
                );
              })}
            </NavGroup>
          ))}
        </nav>
      </aside>

      {/* ── Main column: topbar + routed content ────────────────────────── */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar */}
        <header className="flex h-16 shrink-0 items-center justify-between gap-4 border-b border-hairline bg-surface px-6">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={toggleSidebar}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-expanded={!collapsed}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-hairline bg-surface text-text-secondary transition-colors hover:bg-hover hover:text-text-primary"
            >
              <IconSidebar className="h-[18px] w-[18px]" />
            </button>
            <h1 className="truncate text-[22px] font-semibold text-text-primary">{pageTitle}</h1>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            {profile ? (
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-600">
                  {initials || 'T'}
                </div>
                <div className="hidden text-right leading-tight sm:block">
                  <div className="flex items-center justify-end gap-1.5">
                    <span className="max-w-[180px] truncate text-[13px] font-medium text-text-primary">
                      {profile.name || profile.email}
                    </span>
                    {profile.role ? (
                      <span className="shrink-0 rounded bg-brand-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-brand-600">
                        {profile.role}
                      </span>
                    ) : null}
                  </div>
                  <div className="max-w-[200px] truncate text-[11px] text-text-secondary">
                    {profile.email}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="h-6 w-px bg-hairline" aria-hidden="true" />

            <button
              type="button"
              onClick={adminLogout}
              className="inline-flex items-center gap-1.5 rounded-lg border border-hairline bg-surface px-2.5 py-1.5 text-[13px] font-medium text-text-secondary transition-colors hover:bg-hover hover:text-text-primary"
            >
              <IconLogout className="h-4 w-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
}

/** A collapsible sidebar category: a small UPPERCASE muted header (with a
 *  disclosure caret) over its items. Open by default; the open/closed state is
 *  local to the session. The header is a button so the whole group toggles.
 *
 *  When the whole sidebar is collapsed to the icon rail there is no room for a
 *  text header, so the group renders as a plain icon stack separated by a thin
 *  hairline and the per-group toggle is suppressed (items always show). */
function NavGroup({
  label,
  children,
  collapsed,
}: {
  label: string;
  children: ReactNode;
  collapsed: boolean;
}) {
  const [open, setOpen] = useState(true);

  if (collapsed) {
    return (
      <div className="mb-2 space-y-0.5 border-t border-hairline pt-2 first:border-t-0 first:pt-0">
        {children}
      </div>
    );
  }

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="group flex w-full items-center justify-between rounded-md px-3 py-1 text-left"
        aria-expanded={open}
      >
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">
          {label}
        </span>
        <IconChevron
          className={cn(
            'h-3.5 w-3.5 text-text-muted transition-transform group-hover:text-text-secondary',
            open ? 'rotate-90' : 'rotate-0',
          )}
        />
      </button>
      {open ? <div className="mt-0.5 space-y-0.5">{children}</div> : null}
    </div>
  );
}
